import React, { useState, useEffect } from 'react';
import axios from 'axios';
import LoadingPage from '../../components/LoadingPage';
import { Navigate, useParams } from 'react-router-dom';
import { ProgressBar } from '../../components/PrimereactComponents';
import useDimensions from '../../hooks/useDimensions';
import ChartComponent from '../../components/ChartComponent';
import { useTranslation } from 'react-i18next';
import { redirectErrorPage } from '../../utils/errors';
import Intro from '../../components/common/Intro/Intro';
import Section from '../../components/common/Section/Section';
import MemberCardWrapper from '../../components/common/MemberCardWrapper/MemberCardWrapper';
import { Colors } from '../../Constants';
import {
    AttendanceAnalysisDashboardResponseType,
    ChartComponentDatasetType,
    PartiesStatsResponseType
} from '../../types';
import './AttendanceAnalysis.css';

function AttendanceAnalysis() {
    const [loading, setLoading] = useState(true);
    const [basicData, setBasicData] = useState<AttendanceAnalysisDashboardResponseType | undefined>(undefined);
    const [generalData, setGeneralData] = useState(undefined);
    const [error, setError] = useState<any>(null);
    const [partiesStats, setPartiesStats] = useState<PartiesStatsResponseType | undefined>(undefined);
    const { width } = useDimensions();

    const { t } = useTranslation(['analyses', 'translation']);

    const { municipalitaId, bodyType, zastupitelstvoId } = useParams();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const responses = await Promise.all([
                    axios.get(`${municipalitaId}/${bodyType}/${zastupitelstvoId}/attendanceDashboard`),
                    axios.get(`${municipalitaId}/${bodyType}/${zastupitelstvoId}/generalData`),
                    axios.get(`${municipalitaId}/${bodyType}/${zastupitelstvoId}/partiesStats`)
                ]);

                setBasicData(responses[0].data as AttendanceAnalysisDashboardResponseType);
                setGeneralData(responses[1].data);
                setPartiesStats(responses[2].data as PartiesStatsResponseType);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [bodyType, municipalitaId, zastupitelstvoId]);

    if (loading) {
        return <LoadingPage />;
    }

    if (error) {
        return redirectErrorPage(error);
    }

    if (basicData !== undefined && generalData !== undefined && partiesStats !== undefined) {
        let filtered = partiesStats.filter((obj) => basicData.data.some((item) => item.strana_id === obj.strana_id));
        filtered.sort((a, b) => b.celkova_ucast - a.celkova_ucast);

        const chartData = {
            labels: [''],
            datasets: filtered.map((party) => ({
                label: party.strana_zkratka,
                backgroundColor: party.strana_barva,
                data: [party.celkova_ucast]
            }))
        };

        const chartOptions = {
            indexAxis: 'y',
            plugins: {
                legend: {
                    onHover: function (
                        evt: { native: { target: { style: { cursor: string } } } },
                        item: { datasetIndex: number },
                        legend: {
                            chart: {
                                tooltip: any;
                                data: { datasets: ChartComponentDatasetType[] };
                                update: () => void;
                            };
                        }
                    ) {
                        const tooltip = legend.chart.tooltip;
                        if (tooltip.getActiveElements().length > 0) {
                            tooltip.setActiveElements([], { x: 0, y: 0 });
                        }

                        tooltip.setActiveElements([{ datasetIndex: item.datasetIndex, index: 0 }], { x: 0, y: 0 });

                        evt.native.target.style.cursor = 'pointer';
                        legend.chart.data.datasets.forEach((i, id) => {
                            if (id !== item.datasetIndex) {
                                i.backgroundColor += '30';
                            }
                        });
                        legend.chart.update();
                    },
                    onLeave: function (
                        evt: any,
                        item: { datasetIndex: number },
                        legend: { chart: { data: { datasets: ChartComponentDatasetType[] }; update: () => void } }
                    ) {
                        legend.chart.data.datasets.forEach((i, id) => {
                            if (id !== item.datasetIndex) {
                                i.backgroundColor = i.backgroundColor.slice(0, -2);
                            }
                        });
                        legend.chart.update();
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        beginAtZero: true,
                        callback: (value: string) => {
                            return value + ' %';
                        }
                    },
                    max: 100
                }
            },
            maintainAspectRatio: false,
            aspectRatio: width < 768 ? 1.4 : 1,
            onClick: function (evt: any, item: { datasetIndex: number }[]) {
                if (item[0] !== undefined) {
                    const datasetIndex = item[0].datasetIndex;
                    const basicDataIndex = basicData.data.findIndex(
                        (party) => party.strana_zkratka === filtered[datasetIndex].strana_zkratka
                    );

                    setTimeout(() => {
                        const element = document.getElementById(`${basicDataIndex}-datasetIndex-0`);
                        if (element) {
                            setTimeout(() => element?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 330);
                        }
                    }, 0);
                }
            }
        };

        const adjustSaturationBasedOnAttendance = (attendance: number) => {
            // Values for #6366F1, which is main color
            const baseH = 240;
            const baseS = 85;
            const baseL = 67;

            const adjustedS = attendance === 100 ? baseS : Math.max(10, (baseS * attendance) / 100);
            return `hsl(${baseH}, ${adjustedS}%, ${baseL}%)`;
        };

        return (
            <>
                <Intro title={t('attendance', { ns: 'translation' })} />
                <Section
                    title={t('attendance_by_party', { ns: 'translation' })}
                    subtitle={t('parties-attendance-tooltip')}
                >
                    <ChartComponent
                        type='bar'
                        height={width < 768 ? '300px' : '450px'}
                        data={chartData}
                        options={chartOptions}
                        width={undefined}
                        xTitle={undefined}
                        yTitle={undefined}
                        aria={undefined}
                    />
                </Section>
                <Section
                    title={t('councilMember_attendance', { ns: 'translation' })}
                    subtitle={t('attendance-tooltip')}
                    className='attendance-analysis-members'
                >
                    {basicData?.data.map((party, i) => {
                        let memberGroupsData = [];
                        const activeMembers = party.clenove.filter((member) => member.in_party === 1);
                        const inactiveMembers = party.clenove.filter((member) => member.in_party === 0);

                        if (activeMembers.length > 0) {
                            memberGroupsData.push({
                                partyName: party.strana_zkratka,
                                customCountText: t('active-members', { ns: 'translation' }),
                                partyColor: party.strana_barva,
                                count: activeMembers.length,
                                members: activeMembers.map((member) => {
                                    return {
                                        id: member.id,
                                        firstName: member.jmeno,
                                        lastName: member.prijmeni,
                                        inactive: member.in_party === 0,
                                        gender: member.pohlavi,
                                        customCardContent: (
                                            <ProgressBar
                                                value={member.dochazka.toFixed(2)}
                                                color={adjustSaturationBasedOnAttendance(member.dochazka)}
                                                style={{
                                                    height: '20px',
                                                    width: '100%'
                                                }}
                                            />
                                        )
                                    };
                                })
                            });
                        }

                        if (inactiveMembers.length > 0) {
                            memberGroupsData.push({
                                partyName: party.strana_zkratka,
                                customCountText: t('history', { ns: 'translation' }),
                                partyColor: Colors.darkGray,
                                count: inactiveMembers.length,
                                members: inactiveMembers.map((member) => {
                                    return {
                                        id: member.id,
                                        firstName: member.jmeno,
                                        lastName: member.prijmeni,
                                        gender: member.pohlavi,
                                        customCardContent: (
                                            <ProgressBar
                                                value={member.dochazka.toFixed(2)}
                                                color={Colors.darkGray}
                                                style={{ height: '20px', width: '100%' }}
                                            />
                                        )
                                    };
                                })
                            });
                        }

                        return (
                            <MemberCardWrapper
                                groupName={party.strana_zkratka}
                                count={party.clenove.length}
                                bgColor={party.strana_barva}
                                partyId={party.strana_id}
                                expanded
                                memberGroupsData={memberGroupsData}
                                key={i}
                                tabIndex={i}
                            />
                        );
                    })}
                </Section>
            </>
        );
    } else {
        return <Navigate to='/' />;
    }
}

export default AttendanceAnalysis;
